import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import API from '../services/api';
import { uploadCaseDocuments, describeUploadFailures } from '../services/documentsApi';
import CaseFormWizard from '../components/CaseFormWizard';
import { CaseFormData, initialFormData, developmentProjectsToApi } from '../types/caseForm';
import { plainDateToIso, todayPlain } from '../utils/plainDate';
import { serializeForm, type AutosaveState } from '../utils/formSerialize';
import { formatDistanceToNow } from 'date-fns';

const CreateCase: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showToast } = useToast();
  // Documenter details prefilled from the logged-in user in the initial state (not an effect),
  // so the wizard's unsaved-changes snapshot already includes them.
  const [formData, setFormData] = useState<CaseFormData>(() => ({
    ...initialFormData,
    documentorName: user ? `${user.firstName} ${user.lastName}` : '',
    documentorEmail: user?.email || '',
    documentorOrganization: user?.organization || '',
    // Submission date defaults to today on the user's calendar; shown as a real value, not a placeholder.
    reportedDate: todayPlain(),
  }));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // A new case lives only in this tab until it is submitted, so keep a copy in the browser and
  // offer it back if the page is left or reloaded. Files are not kept (they can't be serialised).
  const draftKey = `ipri:new-case-draft:${user?.id || 'anon'}`;
  const readDraft = (): { savedAt: number; data: Partial<CaseFormData> } | null => {
    try { const raw = localStorage.getItem(draftKey); return raw ? JSON.parse(raw) : null; } catch { return null; }
  };
  const clearDraft = () => { try { localStorage.removeItem(draftKey); } catch { /* storage unavailable */ } };
  const [pendingDraft, setPendingDraft] = useState(() => readDraft());
  const [autosave, setAutosave] = useState<AutosaveState>({ status: 'idle', savedAt: null, where: 'browser' });
  const baseline = useRef<string | null>(null);
  useEffect(() => {
    if (baseline.current === null) { baseline.current = serializeForm(formData); return; }
    if (serializeForm(formData) === baseline.current) return;
    if (pendingDraft) return; // don't overwrite an offered draft before it's restored or discarded
    const t = window.setTimeout(() => {
      try {
        localStorage.setItem(draftKey, JSON.stringify({ savedAt: Date.now(), data: { ...formData, files: [] } }));
        setAutosave({ status: 'saved', savedAt: Date.now(), where: 'browser' });
      } catch { setAutosave({ status: 'error', savedAt: null, where: 'browser' }); }
    }, 1000);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, pendingDraft]);
  const restoreDraft = () => {
    if (!pendingDraft) return;
    setFormData(prev => ({ ...prev, ...pendingDraft.data, files: [] }));
    setPendingDraft(null);
  };
  const discardDraft = () => { clearDraft(); setPendingDraft(null); };

  const handleSubmit = async (asDraft: boolean) => {
    setLoading(true);
    setError('');

    try {
      // Build the start date based on precision
      let processedStartDate = formData.caseStartDate;
      if (formData.caseStartDatePrecision === 'YEAR' && formData.caseStartDate) {
        processedStartDate = `${formData.caseStartDate}-01-01`;
      } else if (formData.caseStartDatePrecision === 'MONTH' && formData.caseStartDate) {
        processedStartDate = `${formData.caseStartDate}-01`;
      }

      const incidentDateISO = processedStartDate ? plainDateToIso(processedStartDate) : null;

      const caseData: Record<string, any> = {
        // Required
        incidentDate: incidentDateISO,
        description: formData.description,
        violationTypes: formData.violationTypes,
        country: formData.country,
        indigenousGroup: formData.indigenousGroup.join(', '),

        // Documenter
        documentorName: formData.documentorName,
        documentorOrganization: formData.documentorOrganization,
        documentorEmail: formData.documentorEmail,
        documentorPhone: formData.documentorPhone,
        isPrimaryData: formData.isPrimaryData,
        dataBasis: formData.dataBasis,
        dataSource: 'Web',
        dataUsageRestriction: formData.dataUsageRestriction,
        willingToShareMoreInfo: formData.willingToShareMoreInfo,
        isLDSFApplicant: formData.isLDSFApplicant,
        ldsfPaymentCompletedDate: formData.isLDSFApplicant && formData.ldsfPaymentCompletedDate ? formData.ldsfPaymentCompletedDate : null,
        sourceInformation: formData.sourceInformation,

        // Incident details
        incidentTime: formData.incidentTime,
        otherViolationTypes: formData.otherViolationTypes,
        otherViolationTypeDetails: formData.otherViolationTypeDetails,
        isCaseOngoing: formData.isCaseOngoing,
        caseStartDate: processedStartDate,
        caseStartDatePrecision: formData.caseStartDatePrecision,
        caseEndDate: formData.caseEndDate || null,
        reportedDate: formData.reportedDate || todayPlain(),

        // Location
        region: formData.region,
        city: formData.city,
        specificLocation: formData.specificLocation,
        latitude: formData.latitude ? parseFloat(formData.latitude) : null,
        longitude: formData.longitude ? parseFloat(formData.longitude) : null,
        indigenousTerritory: formData.indigenousTerritory,

        // Violation details
        violationDetails: formData.violationDetails,

        // Impact
        familyImpact: formData.familyImpact,
        communityImpact: formData.communityImpact,
        impactTypes: formData.impactTypes,
        investigationConducted: formData.investigationConducted,
        investigationDetails: formData.investigationDetails,
        investigationWhyNot: formData.investigationWhyNot,
        advocacyUndertaken: formData.advocacyUndertaken,
        advocacyDetails: formData.advocacyDetails,
        advocacyWhyNot: formData.advocacyWhyNot,

        // Development project
        isRelatedToDevelopmentProject: formData.isRelatedToDevelopmentProject,
        developmentProjects: developmentProjectsToApi(formData.developmentProjects),

        // Victims & perpetrators
        victims: formData.victims,
        perpetrators: formData.perpetrators.filter(p => String(p.name || '').trim()),

        // Metadata
        priority: formData.priority,
        tags: formData.tags,
        isPublic: formData.isPublic,

        submittedById: user?.id,
        status: asDraft ? 'DRAFT' : 'FOR_VALIDATION',
      };

      const response = await API.post('/api/cases', caseData);
      const caseId = response.data.case.id;

      // The case is saved at this point; a document that fails to upload must be said out loud,
      // not logged, or the researcher leaves believing the evidence is attached.
      if (formData.files.length > 0) {
        const outcome = await uploadCaseDocuments(caseId, formData.files);
        const failure = describeUploadFailures(outcome);
        if (failure) showToast('error', failure);
      }

      clearDraft();
      navigate(`/cases/${caseId}`);
    } catch (err: any) {
      const data = err.response?.data;
      let msg = data?.message || 'Failed to create case';
      if (data?.missingFields?.length) {
        msg += ` Missing: ${data.missingFields.join(', ')}`;
      }
      setError(msg);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 font-heading">Create New Case</h1>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          Fill out all required fields to submit a new case
        </p>
      </div>

      {error && (
        <div className="mb-6 rounded-md bg-red-50 p-4">
          <div className="text-sm text-red-800">{error}</div>
        </div>
      )}

      {pendingDraft && (
        <div className="mb-6 rounded-md border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-900 px-4 py-3 text-sm flex flex-wrap items-center justify-between gap-3" role="status">
          <span className="text-gray-800 dark:text-gray-200">
            You have an unfinished case from {formatDistanceToNow(pendingDraft.savedAt, { addSuffix: true })}, kept in this browser.
          </span>
          <span className="flex gap-2">
            <button type="button" onClick={restoreDraft} className="px-3 py-1.5 rounded-md bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700">Restore it</button>
            <button type="button" onClick={discardDraft} className="px-3 py-1.5 rounded-md border border-gray-300 dark:border-gray-600 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700">Discard</button>
          </span>
        </div>
      )}

      <CaseFormWizard
        formData={formData}
        setFormData={setFormData}
        onSubmit={handleSubmit}
        loading={loading}
        cancelTo="/cases"
        autosave={autosave}
        onDiscard={clearDraft}
      />
    </div>
  );
};

export default CreateCase;
